// src/App.tsx
import * as React from 'react';
import { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { DataProvider } from './contexts/DataContext';
import { MunicipalDataProvider } from './contexts/MunicipalDataContext';
import { MapStateProvider } from './hooks/useMapState';
import { ThemeProvider } from './contexts/ThemeContext';
import { ViewProvider } from './contexts/ViewContext';
import { ViewMode, ChartType } from './types/ViewTypes';

// Lazy load page components for code splitting
// This reduces initial bundle size and improves Time to Interactive (TTI)
const MapView = lazy(() => import('./pages/MapView'));
const MunicipalView = lazy(() => import('./pages/MunicipalView'));
const AnalysisView = lazy(() => import('./pages/AnalysisView'));
const ComparisonView = lazy(() => import('./pages/ComparisonView'));
const AboutView = lazy(() => import('./pages/AboutView'));
const ForecastView = lazy(() => import('./pages/ForecastView'));
const AdvancedForecastView = lazy(() => import('./pages/AdvancedForecastView'));
const SimilarityView = lazy(() => import('./pages/SimilarityView'));

// Eagerly import layout components (always needed)
import Navbar from './components/Layout/Navbar';
import Footer from './components/Layout/Footer';
import { RouteLoadingSkeleton } from './components/UI/LoadingSkeletons';
// import { PerformanceMonitor } from './components/PerformanceMonitor'; // Temporarily disabled

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <DataProvider>
        <MunicipalDataProvider>
          <ViewProvider initialViewMode={ViewMode.MAP} initialChartType={ChartType.LINE}>
            <MapStateProvider>
              <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <div className="app-container">
                  <Navbar />
                  <main className="main-content">
                    {/* Suspense boundary for lazy-loaded route components */}
                    <Suspense fallback={<RouteLoadingSkeleton />}>
                      <Routes>
                        <Route path="/" element={<MunicipalView />} />
                        <Route path="/municipal" element={<MunicipalView />} />
                        <Route path="/regional" element={<MapView />} />
                        <Route path="/analysis" element={<AnalysisView />} />
                        <Route path="/comparison" element={<ComparisonView />} />
                        <Route path="/similarity" element={<SimilarityView />} />
                        <Route path="/forecast" element={<ForecastView />} />
                        <Route path="/advanced-forecast" element={<AdvancedForecastView />} />
                        <Route path="/about" element={<AboutView />} />
                      </Routes>
                    </Suspense>
                  </main>
                  <Footer />
                  {/* <PerformanceMonitor /> */}
                </div>
              </Router>
            </MapStateProvider>
          </ViewProvider>
        </MunicipalDataProvider>
      </DataProvider>
    </ThemeProvider>
  );
};

export default App;
